import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AMBIENTS } from "@/lib/sessions";
import { playAmbient, stopAmbient, setVolume, isAudioSupported, getCurrentAmbient, previewAmbient } from "@/lib/audio";
import { checkAccess, isFreeAmbient } from "@/lib/entitlement";
import { useAuth } from "@/lib/auth";
import SessionIllustration from "@/components/SessionIllustration";
import CategoryIcon from "@/components/CategoryIcon";
import { ChevronLeft, Play, Pause, Volume2, Lock, Headphones } from "lucide-react";

export default function Music() {
  const nav = useNavigate();
  const { entitlement } = useAuth();
  const [playingId, setPlayingId] = useState(null);
  const [vol, setVol] = useState(0.6);
  const [error, setError] = useState(null);
  const stoppedRef = useRef(false);

  useEffect(() => {
    return () => { stopAmbient({ fade: false }); };
  }, []);

  async function toggle(id) {
    if (playingId === id) {
      stopAmbient({ fade: true, fadeSeconds: 1.5 });
      setPlayingId(null);
      return;
    }
    // Vérification serveur avant lecture
    const access = await checkAccess("ambient", id);
    if (!access.allowed) {
      if (access.reason === "auth_required") nav("/connexion", { state: { from: "/musique" } });
      else nav("/premium");
      return;
    }
    if (!isAudioSupported()) {
      setError("L'audio n'est pas disponible dans ce navigateur.");
      return;
    }
    const res = await playAmbient(id, vol);
    if (!res.ok) { setError("Impossible de démarrer l'audio."); return; }
    setError(null);
    setPlayingId(id);
  }

  function onVol(v) { setVol(v); setVolume(v); }

  async function onPreview(id, e) {
    e.stopPropagation();
    if (!isAudioSupported()) return;
    await previewAmbient(id, 6);
    setPlayingId(null); // l'aperçu s'arrête tout seul
  }

  return (
    <div className="animate-fade-in pt-6">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="flex items-center gap-1 text-[color:var(--ivoire-muted)] text-sm mb-4">
        <ChevronLeft size={16} /> Retour
      </button>
      <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">Musique douce & ASMR</h1>
      <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">
        Ambiances générées en direct. Une seule à la fois.
      </p>

      {error && <p data-testid="music-error" className="mt-4 text-sm text-amber-400">{error}</p>}

      <div className="mt-6 grid grid-cols-2 gap-3" data-testid="music-list">
        {AMBIENTS.map((a) => {
          const free = isFreeAmbient(a.id);
          const locked = !free && !entitlement.premium;
          const isActive = playingId === a.id;
          return (
          <button
            key={a.id}
            data-testid={`music-${a.id}`}
            onClick={() => toggle(a.id)}
            className={`relative overflow-hidden rounded-2xl border transition group ${
              isActive ? "border-[color:var(--sauge)]" : "border-[color:var(--border)] hover:border-[color:var(--sauge)]/40"
            }`}
            style={{ aspectRatio: "1 / 1.1" }}
          >
            <div className="absolute inset-0" style={{
              background: a.id === "pink"
                ? "radial-gradient(ellipse at 30% 20%, rgba(220,180,190,0.20), rgba(20,34,53,0.85))"
                : a.id === "brown"
                ? "radial-gradient(ellipse at 30% 20%, rgba(150,110,90,0.25), rgba(20,34,53,0.90))"
                : a.id === "rain"
                ? "radial-gradient(ellipse at 30% 20%, rgba(140,170,190,0.28), rgba(20,34,53,0.90))"
                : "radial-gradient(ellipse at 30% 20%, rgba(120,170,180,0.28), rgba(20,34,53,0.90))",
            }} />
            <div className="absolute inset-0 flex flex-col items-center justify-between p-4">
              <div className="w-full flex items-start justify-between">
                <CategoryIcon id={a.id} size={34} />
                {free && (
                  <span className="text-[9px] font-mono tracking-widest uppercase bg-[color:var(--sauge)]/25 text-[color:var(--sauge-light)] px-2 py-0.5 rounded-full">
                    Découverte
                  </span>
                )}
                {locked && (
                  <span data-testid={`locked-tag-${a.id}`} className="text-[9px] font-mono tracking-widest uppercase text-[color:var(--ivoire-subtle)]">
                    Premium
                  </span>
                )}
              </div>
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                  isActive ? "bg-[color:var(--sauge)] text-[color:var(--bg-deep)] scale-105" : "bg-[color:var(--ivoire)]/95 text-[color:var(--bg-deep)] group-hover:scale-105"
                }`}
                style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
              >
                {locked
                  ? <Lock size={18} />
                  : isActive
                    ? <Pause size={20} fill="currentColor" />
                    : <Play size={20} fill="currentColor" className="ml-0.5" />
                }
              </div>
              <div className="w-full text-left">
                <div className="font-serif text-base text-[color:var(--ivoire)] leading-tight">{a.label}</div>
                <div className="text-[10px] text-[color:var(--ivoire-muted)] mt-0.5 line-clamp-1">{a.description}</div>
                {!locked && !isActive && (
                  <button
                    data-testid={`preview-${a.id}`}
                    onClick={(e) => onPreview(a.id, e)}
                    className="mt-2 inline-flex items-center gap-1 text-[10px] text-[color:var(--sauge-light)] underline underline-offset-4"
                  >
                    <Headphones size={11} /> Aperçu 6 s
                  </button>
                )}
              </div>
            </div>
          </button>
        )})}
      </div>

      {playingId && (
        <div className="mt-8 flex items-center gap-3 rounded-2xl border border-[color:var(--border)] bg-[#142235]/70 p-4">
          <Volume2 size={16} className="text-[color:var(--ivoire-muted)]" />
          <input
            data-testid="music-volume"
            type="range" min="0" max="1" step="0.01"
            value={vol}
            onChange={(e) => onVol(parseFloat(e.target.value))}
            className="flex-1 accent-[color:var(--sauge)]"
            aria-label="Volume"
          />
        </div>
      )}
    </div>
  );
}
